import { queryClient } from "src/lib/react-query/queryClient";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { SKILLS as COLLECTION_NAME_SKILLS } from "@consts";
import { CONFIGURATION_EDITOR_MESSAGES } from "src/pages/ConfigurationEditor.consts";
import { GENERIC_ERROR_MESSAGES } from "src/components/collections/consts";

const UNPROCESSABLE_ENTITY = 422;

// Never surface the raw server error. A 422 means the edited config is invalid, so
// we tell the user that generically; anything else is treated as a transient failure.
const getSaveErrorMessage = (err) =>
  err?.response?.status === UNPROCESSABLE_ENTITY
    ? CONFIGURATION_EDITOR_MESSAGES.INVALID_CONFIG_FORMAT
    : GENERIC_ERROR_MESSAGES.SAVE;

const getChangedSkills = (editedSkills, originalConfig) => {
  const originalByKey = new Map(
    (originalConfig?.skills || []).map(skill => [skill.key, skill])
  );
  return editedSkills.filter(skill => {
    const original = originalByKey.get(skill.key);
    if (!original) return true;
    return JSON.stringify(skill.configuration) !== JSON.stringify(original.configuration);
  });
};

const createConfigEditorSlice = (set, get) => ({
  configEditorConfigChanged: false,
  editedConfigContent: "",
  originalConfig: null,
  configEditorSaveError: "",
  setConfigEditorConfigChanged: value => set({ configEditorConfigChanged: value }),
  setEditedConfigContent: value => set({ editedConfigContent: value }),
  setOriginalConfig: value => set({ originalConfig: value }),
  setConfigEditorSaveError: value => set({ configEditorSaveError: value }),
  resetConfigEditor: () => set({
    configEditorConfigChanged: false,
    editedConfigContent: "",
    originalConfig: null,
    configEditorSaveError: ""
  }),
  saveConfigEditor: async (saveSkillMutation) => {
    const { selectedAssistant, editedConfigContent, originalConfig, userData } = get();
    const assistantId = selectedAssistant?.assistantId;
    if (!assistantId) return false;

    let parsedConfig;
    try {
      parsedConfig = JSON.parse(editedConfigContent);
    } catch (err) {
      console.error("Invalid JSON in configuration editor", err);
      return false;
    }

    const changedSkills = getChangedSkills(parsedConfig.skills || [], originalConfig);
    if (changedSkills.length === 0) {
      set({ configEditorConfigChanged: false });
      return true;
    }

    set({ isSavingChanges: true, configEditorSaveError: "" });
    // Server persists one skill at a time — save each changed skill sequentially.
    // URL slots mirror the collection save: spec key = skill.configuration.key,
    // instance key = skill.key.
    for (const skill of changedSkills) {
      const payload = {
        instance_config: skill,
        userData: { name: userData.name, email: userData.email }
      };
      let result;
      try {
        // eslint-disable-next-line no-await-in-loop
        result = await saveSkillMutation.mutateAsync({
          assistantId,
          skillSpecKey: skill.configuration.key,
          skillInstanceKey: skill.key,
          payload
        });
      } catch (err) {
        // Surface the failure (e.g. a 422 when the edited config is invalid) so the
        // user sees why the save was rejected instead of it failing silently.
        console.error("Failed to save skill configuration", err);
        set({ isSavingChanges: false, configEditorSaveError: getSaveErrorMessage(err) });
        return false;
      }

      // Write the saved skill straight into the single-collection cache so the skill
      // page reads the new value immediately — no stale flash, no loader. The page
      // renders `draftConfig` in edit mode (see setCollection).
      const savedConfig = result?.updatedCollection || skill;
      queryClient.setQueryData(
        [FETCH_QUERY_KEYS.COLLECTION, assistantId, COLLECTION_NAME_SKILLS, skill.configuration.key, skill.key],
        (oldData) => ({ ...oldData, draftConfig: savedConfig })
      );
    }

    try {
      set({
        isSavingChanges: false,
        configEditorConfigChanged: false,
        originalConfig: parsedConfig
      });
      // The skills list and the fetched configuration can refresh in the background;
      // the single-skill cache was already updated above so that page shows no delay.
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.COLLECTIONS] });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.ASSISTANT_CONFIGURATION, assistantId] }),
        queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.DRAFT] })
      ]);
      return true;
    } catch (err) {
      // The skills were already persisted above; only cache refresh failed. Report
      // success so the user isn't told the save failed when it didn't.
      console.error("Saved skills but failed to refresh caches", err);
      set({ isSavingChanges: false, configEditorConfigChanged: false, originalConfig: parsedConfig });
      return true;
    }
  }
});

export default createConfigEditorSlice;
